import React, { useCallback, useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { getDisplayUrl, getTenantActions, getTenantDisplayName } from '../tenantHelpers';
import DiscardUnsavedChangesModal from './DiscardUnsavedChangesModal';
import TenantActionMenu from './TenantActionMenu';
import TenantDrawerContent from './TenantDrawerContent';
import TenantStatusPill from './TenantStatusPill';

const DrawerBodySkeleton = () => (
  <div className="stm-drawer-sections">
    {Array.from({ length: 4 }).map((_, sectionIndex) => (
      <section key={`drawer-skeleton-${sectionIndex}`} className="stm-drawer-section stm-drawer-section--card">
        <span className="stm-skeleton stm-skeleton--md" style={{ width: 120, marginBottom: 12 }} />
        <span className="stm-skeleton stm-skeleton--sm" style={{ width: '100%', marginBottom: 8 }} />
        <span className="stm-skeleton stm-skeleton--sm" style={{ width: '85%', marginBottom: 8 }} />
        <span className="stm-skeleton stm-skeleton--sm" style={{ width: '70%' }} />
      </section>
    ))}
  </div>
);

const TenantDetailsDrawer = ({
  tenant,
  isOpen,
  mode,
  detailLoading,
  detailLoaded,
  detailError,
  onRetryDetail,
  onClose,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onAction,
  getActions,
  isSaving,
  pendingOpen,
  onConfirmDiscard,
  onManageTenantFeatures,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [discardAction, setDiscardAction] = useState('close');

  const isEditing = mode === 'edit';
  const showDetailContent = detailLoaded && !detailError;
  const showDetailSkeleton = detailLoading && !detailLoaded;
  const contentTenant = showDetailContent ? tenant : null;

  useEffect(() => {
    if (isOpen) {
      setIsDirty(false);
      setShowDiscard(false);
    }
  }, [isOpen, tenant?.id]);

  useEffect(() => {
    if (!isEditing) setIsDirty(false);
  }, [isEditing]);

  const handleDirtyChange = useCallback((dirty) => {
    setIsDirty(dirty);
  }, []);

  const requestDiscard = (action) => {
    if (isEditing && isDirty) {
      setDiscardAction(action);
      setShowDiscard(true);
      return true;
    }
    return false;
  };

  const handleClose = () => {
    if (requestDiscard('close')) return;
    onClose();
  };

  const handleCancelEdit = () => {
    if (requestDiscard('cancelEdit')) return;
    onCancelEdit();
  };

  const handleDiscard = () => {
    setShowDiscard(false);
    setIsDirty(false);
    if (discardAction === 'close') {
      onClose();
    } else if (discardAction === 'cancelEdit') {
      onCancelEdit();
    } else if (discardAction === 'switchTenant' && pendingOpen) {
      onConfirmDiscard?.(pendingOpen);
    }
  };

  useEffect(() => {
    if (!pendingOpen || !isEditing) return;
    if (isDirty) {
      setDiscardAction('switchTenant');
      setShowDiscard(true);
    } else {
      onConfirmDiscard?.(pendingOpen);
    }
  }, [pendingOpen, isEditing, isDirty, onConfirmDiscard]);

  if (!isOpen || !tenant) return null;

  const actions = getActions ? getActions(tenant) : getTenantActions(tenant);
  const menuActions = actions.filter((action) => !['view', 'edit'].includes(action.key));
  const canEdit = actions.some((action) => action.key === 'edit');

  return (
    <>
      <div className="stm-drawer-overlay" onClick={handleClose}>
        <aside className="stm-drawer" onClick={(event) => event.stopPropagation()}>
          <header className="stm-drawer__header stm-drawer__header--sticky">
            <div className="stm-drawer__header-main">
              <div className="stm-drawer__header-top">
                <h2>{getTenantDisplayName(tenant)}</h2>
                <div className="stm-drawer__header-actions">
                  {!isEditing && canEdit && showDetailContent && (
                    <button type="button" className="stm-btn stm-btn--secondary stm-btn--sm" onClick={onStartEdit}>
                      Edit tenant
                    </button>
                  )}
                  {!isEditing && menuActions.length > 0 && showDetailContent && (
                    <TenantActionMenu tenant={tenant} actions={menuActions} onAction={onAction} />
                  )}
                  <button type="button" className="stm-icon-btn stm-icon-btn--drawer" onClick={handleClose} disabled={isSaving} aria-label="Close drawer">
                    <FaTimes />
                  </button>
                </div>
              </div>
              {!isEditing && (
                <p className="stm-drawer__meta">
                  <span>{tenant.tenantType}</span>
                  <span className="stm-drawer__meta-sep" aria-hidden="true">·</span>
                  <TenantStatusPill tenant={tenant} size="sm" showDot />
                </p>
              )}
              {isEditing && (
                <p className="stm-drawer__meta">{tenant.tenantType}</p>
              )}
              <a
                className="stm-drawer__url"
                href={tenant.tenantUrl}
                target="_blank"
                rel="noreferrer"
                title={tenant.tenantUrl}
              >
                {getDisplayUrl(tenant.tenantUrl)}
              </a>
              {isEditing && isDirty && (
                <p className="stm-drawer__unsaved">Unsaved changes</p>
              )}
            </div>
          </header>

          <div className={`stm-drawer__body ${isEditing ? 'stm-drawer__body--edit' : ''}`}>
            {showDetailSkeleton && <DrawerBodySkeleton />}
            {detailError && !detailLoading && (
              <div className="stm-drawer-detail-error">
                <p>{detailError}</p>
                <button type="button" className="stm-btn stm-btn--secondary stm-btn--sm" onClick={onRetryDetail}>
                  Retry
                </button>
              </div>
            )}
            {showDetailContent && (
              <TenantDrawerContent
                tenant={contentTenant}
                isEditing={isEditing}
                onSave={onSaveEdit}
                onDirtyChange={handleDirtyChange}
                onAction={onAction}
                onManageTenantFeatures={onManageTenantFeatures}
              />
            )}
          </div>

          {isEditing && showDetailContent && (
            <footer className="stm-drawer__footer stm-drawer__footer--edit">
              <button type="button" className="stm-btn stm-btn--secondary" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </button>
              <button
                type="submit"
                form="tenant-drawer-form"
                className="stm-btn stm-btn--primary"
                disabled={isSaving || !isDirty}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </footer>
          )}
        </aside>
      </div>

      <DiscardUnsavedChangesModal
        tenant={tenant}
        isOpen={showDiscard}
        onKeepEditing={() => setShowDiscard(false)}
        onDiscard={handleDiscard}
      />
    </>
  );
};

export default TenantDetailsDrawer;
